/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useEffect, useState } from 'react'
import { useServiceContext } from '../../../../root'

interface PaginateProps {
  totalItems: number
  totalItemsFake?: boolean | number
  page: number
  pageChanged: (newPage: number) => void
  itemsPerPage: number
  itemsPerPageChanged: (newValue: number) => void
  txtTotalItems?: string
  isEosWorkflowProcess?: boolean
  level?: string
  isReview?: boolean
}

const Paginate: React.FC<PaginateProps> = (props) => {
  const context = useServiceContext()
  const { translate } = context.service.filter

  const possiblePageSizes = props.isEosWorkflowProcess ? [10, 15, 25] : [10, 15, 25, 50, 100, 200]

  const [options, setOptions] = useState({
    totalPages: 1,
    currentPage: 1,
    vmPage: 1
  })
  const [actualPageSize, setActualPageSize] = useState<number>(0)
  const [itemsPerPage, setItemsPerPage] = useState<number>(0)

  useEffect(() => {
    const pageSize = props.itemsPerPage ?? context.service.PageSize!.get()
    setActualPageSize(pageSize)
    setItemsPerPage(pageSize)
  }, [])

  useEffect(() => {
    setOptions((prev) => ({ ...prev, currentPage: props.page, vmPage: props.page }))
  }, [props.page])

  useEffect(() => {
    setItemsPerPage(props.itemsPerPage)
    setOptions((prev) => ({ ...prev, totalPages: getTotalPagesFrom(props.itemsPerPage) }))
  }, [props.itemsPerPage])

  useEffect(() => {
    setOptions((prev) => ({ ...prev, totalPages: getTotalPagesFrom(itemsPerPage) }))
  }, [props.totalItems])

  const getTotalPagesFrom = (ipp: number): number => {
    return Math.ceil(props.totalItems / ipp) || 1
  }

  const getTotalPages = (): number => {
    return Math.ceil(props.totalItems / itemsPerPage) || 1
  }

  const getTotalItems = (): number | boolean => {
    if (typeof props.totalItemsFake === 'boolean') return props.totalItemsFake
    if (typeof props.totalItemsFake === 'number') return props.totalItemsFake
    return props.totalItems < 0 ? 0 : props.totalItems
  }

  const pageUpdated = (newValue: number) => {
    setOptions((prev) => ({ ...prev, currentPage: newValue, vmPage: newValue }))
    setTimeout(() => {
      props.pageChanged(newValue)
    }, 500)
  }

  const changedPageSize = (newPageSizeValue: string) => {
    const val = parseInt(newPageSizeValue, 10)
    context.service.PageSize!.set(val)
    props.itemsPerPageChanged(val)
    setActualPageSize(val)
    setItemsPerPage(val)
    setOptions((prev) => ({ ...prev, currentPage: 1 }))
    pageUpdated(1)
  }

  const prevPage = () => {
    if (options.totalPages === 1 || options.currentPage === 1) return
    pageUpdated(options.currentPage - 1)
  }

  const nextPage = () => {
    if (options.totalPages === 1 || options.currentPage === options.totalPages) return
    pageUpdated(options.currentPage + 1)
  }

  const firstPage = () => {
    if (options.totalPages === 1 || options.currentPage === 1) return
    pageUpdated(1)
  }

  const lastPage = () => {
    if (options.totalPages === 1 || options.currentPage === options.totalPages) return
    pageUpdated(getTotalPages())
  }

  const changePage = (event: React.FormEvent) => {
    event.preventDefault()
    const newPageValue = options.vmPage
    if (newPageValue >= 1 && newPageValue <= getTotalPages()) {
      setOptions((prev) => ({ ...prev, currentPage: newPageValue }))
      pageUpdated(newPageValue)
    }
  }

  const totalItemsDisplay = getTotalItems()

  return (
    <div id="pageableData">
      <div className="paginate clearfix">
        {!!totalItemsDisplay && (
          <div className="total-results">
            <b>{typeof totalItemsDisplay === 'number' ? totalItemsDisplay : ''}</b>{' '}
            <span>{translate(props.txtTotalItems || 'PAGINATION_TOTAL_RESULTS')}</span>
          </div>
        )}
        <div className="page-size-configurator">
          <span>{translate('PAGINATION_PER_PAGE')}</span>
          <select
            id="i-page-size-selector"
            name="itemsPerPage"
            className="form-control"
            value={actualPageSize}
            onChange={(e) => changedPageSize(e.target.value)}
          >
            {possiblePageSizes.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>
        <ul className="controls">
          <li>
            <a className={options.currentPage === 1 ? 'disabled first' : 'first'} onClick={firstPage}>
              <i className="fa fa-angle-double-left"></i>
            </a>
          </li>
          <li>
            <a className={options.currentPage === 1 ? 'disabled previous' : 'previous'} onClick={prevPage}>
              <i className="fa fa-angle-left"></i>
            </a>
          </li>
          <li className="counter">
            <form name="pForm" onSubmit={changePage}>
              <input
                value={options.vmPage}
                type="number"
                min={1}
                max={getTotalPages()}
                className="current"
                autoComplete="off"
                onChange={(e) => {
                  setOptions((prev) => ({ ...prev, vmPage: parseInt(e.target.value, 10) }))
                }}
              />
              <span> {translate('PAGINATION_OF_PREPOSITION')} </span>
              <span className="total-pages">{getTotalPages()}</span>
            </form>
          </li>
          <li>
            <a className={options.totalPages === options.currentPage ? 'disabled next' : 'next'} onClick={nextPage}>
              <i className="fa fa-angle-right"></i>
            </a>
          </li>
          <li>
            <a className={options.totalPages === options.currentPage ? 'disabled last' : 'last'} onClick={lastPage}>
              <i className="fa fa-angle-double-right"></i>
            </a>
          </li>
        </ul>
      </div>
    </div>
  )
}

export default Paginate
